import { useEffect, useRef, useState } from "react";
import {
  Truck, Route, Package, BarChart3, Image as ImageIcon, Globe2, Users,
  Send, Smile, X, Loader2, ChevronDown, MessageSquare, Hash,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { HubAvatar } from "@/components/social/HubAvatar";
import { useHubSettings } from "@/hooks/hub/useHubSettings";
import { useHubFeeds, HUB_GENERAL_SLUG } from "@/hooks/hub/useHubFeeds";

/**
 * LR HUB composer — the ONLY posting surface. Text is submitted to the
 * `moderate-hub-post` edge function by the parent (see SocialHubPage);
 * this component owns the input state only.
 *
 * Hub-isolated: reads `hub_settings` for the author avatar, nothing else.
 */

export const HUB_CONTENT_TYPES = [
  { value: "general", label: "General", icon: MessageSquare },
  { value: "capacity_update", label: "Capacity Update", icon: Truck },
  { value: "lane_request", label: "Lane Request", icon: Route },
  { value: "shipment_need", label: "Shipment Need", icon: Package },
  { value: "poll", label: "Poll", icon: BarChart3 },
] as const;

export const MAX_HUB_BODY = 3000;

/** Lightweight built-in emoji set — inserted at the caret. */
const EMOJIS = [
  "🙂", "👍", "🙌", "🔥", "✅", "❗", "🚛", "🚚", "❄️", "📦",
  "📍", "📈", "📉", "⏱️", "🤝", "💪", "🎯", "☎️", "🗺️", "🧊",
];

export interface HubComposerSubmit {
  body: string;
  content_type: string;
  /** Topic feed the post is created in (server resolves + gates it). */
  feed_slug: string;
  /** Display label for the optimistic card only. */
  feed_name?: string;
}

export function HubComposer({
  onSubmit,
  submitting,
  notice,
  onDismissNotice,
  prefill,
  onPrefillApplied,
  currentFeedSlug,
}: {
  onSubmit: (input: HubComposerSubmit) => void;
  submitting?: boolean;
  notice?: { tone: "error" | "info"; text: string } | null;
  onDismissNotice?: () => void;
  prefill?: { body: string; content_type: string } | null;
  onPrefillApplied?: () => void;
  /** Feed currently being viewed — used as the default target when postable. */
  currentFeedSlug?: string | null;
}) {
  const { data: settings } = useHubSettings();
  const { postableFeeds, canChooseFeed } = useHubFeeds();
  const [feedSlug, setFeedSlug] = useState<string | null>(null);
  const [body, setBody] = useState("");
  const [contentType, setContentType] = useState<string>("capacity_update");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!prefill) return;
    const el = taRef.current;
    const normalizedType = HUB_CONTENT_TYPES.some((c) => c.value === prefill.content_type)
      ? prefill.content_type
      : "general";

    if (body.trim().length > 0) {
      const replace = window.confirm("Replace your current draft with this template?\n(Cancel appends it instead.)");
      if (replace) {
        setBody(prefill.body.slice(0, MAX_HUB_BODY));
      } else {
        const joined = body.trimEnd() + "\n\n" + prefill.body;
        setBody(joined.slice(0, MAX_HUB_BODY));
      }
    } else {
      setBody(prefill.body.slice(0, MAX_HUB_BODY));
    }
    setContentType(normalizedType);

    requestAnimationFrame(() => {
      el?.focus();
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    onPrefillApplied?.();
  }, [prefill]);

  // Resolve the target feed: explicit pick → current feed (if postable) → General.
  const defaultFeed =
    postableFeeds.find((f) => f.slug === currentFeedSlug) ??
    postableFeeds.find((f) => f.slug === HUB_GENERAL_SLUG) ??
    postableFeeds[0] ??
    null;
  const activeFeed = postableFeeds.find((f) => f.slug === feedSlug) ?? defaultFeed;

  const active = HUB_CONTENT_TYPES.find((c) => c.value === contentType) ?? HUB_CONTENT_TYPES[0];
  const canPost = body.trim().length > 0 && !submitting;

  function insertEmoji(emoji: string) {
    const el = taRef.current;
    if (!el) {
      setBody((b) => b + emoji);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + emoji + body.slice(end);
    setBody(next.slice(0, MAX_HUB_BODY));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  }

  function submit() {
    if (!canPost) return;
    onSubmit({
      body: body.trim(),
      content_type: contentType,
      feed_slug: activeFeed?.slug ?? HUB_GENERAL_SLUG,
      feed_name: activeFeed?.name ?? "General",
    });
    setBody("");
  }

  return (
    <section
      className="rounded-xl border border-border bg-card p-4"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      <div className="flex items-start gap-3">
        <HubAvatar
          username={settings?.display_name || "LR"}
          avatarPath={settings?.avatar_status === "approved" ? settings.avatar_path : null}
          size="md"
        />
        <textarea
          ref={taRef}
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX_HUB_BODY))}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
          }}
          rows={3}
          placeholder="Share capacity, ask your network, or post a lane update..."
          className="min-h-[76px] flex-1 resize-y rounded-lg border border-border bg-background px-3 py-2.5 text-[12.5px] leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-teal/40"
        />
      </div>

      {/* Content type selector */}
      <div className="mt-3.5 grid grid-cols-2 border-y border-border py-1 sm:grid-cols-3 lg:grid-cols-5">
        {HUB_CONTENT_TYPES.map((c) => (
          <button
            key={c.value}
            type="button"
            onClick={() => setContentType(c.value)}
            className={cn(
              "flex items-center justify-center gap-1.5 rounded-md px-2 py-2 text-[11.5px] font-semibold transition-colors",
              contentType === c.value
                ? "bg-brand-teal-subtle text-brand-teal-deep"
                : "text-muted-foreground hover:text-brand-teal-deep",
            )}
          >
            <c.icon className="h-3.5 w-3.5" />
            <span className="truncate">{c.label}</span>
          </button>
        ))}
        <button
          type="button"
          disabled
          title="Coming soon"
          className="flex cursor-not-allowed items-center justify-center gap-1.5 px-2 py-2 text-[11.5px] font-semibold text-muted-foreground/50"
        >
          <ImageIcon className="h-3.5 w-3.5" />
          <span className="truncate">Photo/Doc</span>
          <span className="rounded bg-muted px-1 py-0.5 text-[9px] font-bold uppercase">Soon</span>
        </button>
      </div>

      {notice ? (
        <div
          className={cn(
            "mt-3 flex items-start justify-between gap-2 rounded-lg border px-3 py-2 text-[11.5px]",
            notice.tone === "error"
              ? "border-danger/30 bg-danger/10 text-danger"
              : "border-brand-amber/40 bg-brand-amber/10 text-foreground",
          )}
        >
          <span>{notice.text}</span>
          {onDismissNotice ? (
            <button type="button" onClick={onDismissNotice}>
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {/* Audience */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-[11.5px] font-semibold text-foreground">
                <Globe2 className="h-3.5 w-3.5 text-muted-foreground" /> Everyone
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem className="text-[12px]">
                <Globe2 className="mr-2 h-3.5 w-3.5" /> Everyone
              </DropdownMenuItem>
              <DropdownMenuItem disabled className="text-[12px]">
                <Users className="mr-2 h-3.5 w-3.5" /> My Network
                <span className="ml-auto text-[9px] font-bold uppercase">Soon</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Feed target — dropdown for admins, static label for everyone else */}
          {canChooseFeed ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-[11.5px] font-semibold text-foreground">
                  <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                  {activeFeed?.name ?? "General"}
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {postableFeeds.map((f) => (
                  <DropdownMenuItem
                    key={f.slug}
                    className="text-[12px]"
                    onClick={() => setFeedSlug(f.slug)}
                  >
                    <Hash className="mr-2 h-3.5 w-3.5" /> {f.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-1.5 text-[11.5px] font-semibold text-muted-foreground">
              <Hash className="h-3.5 w-3.5" /> Posting to: {activeFeed?.name ?? "General"}
            </span>
          )}

          {/* Emoji */}
          <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Insert emoji"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-brand-teal-deep"
              >
                <Smile className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[228px] p-2">
              <div className="grid grid-cols-5 gap-1">
                {EMOJIS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => {
                      insertEmoji(e);
                      setEmojiOpen(false);
                    }}
                    className="rounded-md py-1 text-[17px] hover:bg-muted"
                  >
                    {e}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <span className="hidden text-[10.5px] text-muted-foreground sm:inline">
            {active.label}
          </span>
        </div>

        <div className="flex items-center gap-2.5">
          <span
            className={cn(
              "text-[10.5px] tabular-nums",
              body.trim().length >= MAX_HUB_BODY
                ? "font-bold text-danger"
                : body.trim().length >= MAX_HUB_BODY - 50
                  ? "font-semibold text-brand-amber"
                  : "text-muted-foreground",
            )}
          >
            {body.trim().length}/{MAX_HUB_BODY}
          </span>
          <Button
            size="sm"
            disabled={!canPost}
            onClick={submit}
            className="h-8 gap-1.5 bg-brand-teal-deep px-4 text-[12px] font-bold text-white hover:bg-brand-teal-deep/85"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Post Update
          </Button>
        </div>
      </div>
    </section>
  );
}

export default HubComposer;
